import React from 'react';
import { Typography, Paper, Grid, List, ListItem, ListItemText, Chip, Divider, useTheme, useMediaQuery } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { Fortalezas as FortalezasType } from '../../../types/consolidatedReport.types';

interface FortalezasProps {
  data: FortalezasType;
}

const Fortalezas: React.FC<FortalezasProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        p: isMobile ? 2 : 3, 
        mb: 3,
        width: '100%',
        overflow: 'hidden'
      }}
    >
      <Typography 
        variant={isMobile ? 'body1' : 'h6'} 
        gutterBottom 
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <StarIcon color="success" fontSize={isMobile ? 'small' : 'medium'} />
        Fortalezas y Logros
      </Typography>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Categorías Destacadas */}
        {data.categorias_destacadas.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography 
              variant={isMobile ? 'body2' : 'subtitle1'} 
              sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}
            >
              Categorías Destacadas (≥85%)
            </Typography>
            <List dense={isMobile}>
              {data.categorias_destacadas.map((cat, index) => (
                <React.Fragment key={cat.id}>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontSize: isMobile ? 12 : 14 }}>{cat.name}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ fontSize: isMobile ? 10 : 12 }}>
                          {cat.average.toFixed(1)}%
                        </Typography>
                      }
                    />
                    <Chip 
                      label={`${cat.average.toFixed(0)}%`} 
                      color="success" 
                      size="small"
                      sx={{ fontSize: isMobile ? 9 : 11 }}
                    />
                  </ListItem>
                  {index < data.categorias_destacadas.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
        )}

        {/* Top 3 Preguntas Mejores */}
        {data.top_preguntas_mejores.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography 
              variant={isMobile ? 'body2' : 'subtitle1'} 
              sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}
            >
              Prácticas Destacadas
            </Typography>
            <List dense={isMobile}>
              {data.top_preguntas_mejores.map((pregunta, index) => (
                <React.Fragment key={pregunta.id}>
                  <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontSize: isMobile ? 11 : 13 }}>
                          {pregunta.text}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: isMobile ? 9 : 11 }}>
                            {pregunta.category}
                          </Typography>
                          <Typography variant="caption" color="success.main" sx={{ fontSize: isMobile ? 9 : 11 }}>
                            {pregunta.average.toFixed(1)}% • {pregunta.recurrence} veces
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  {index < data.top_preguntas_mejores.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
        )}
      </Grid>

      {data.categorias_destacadas.length === 0 && data.top_preguntas_mejores.length === 0 && (
        <Typography 
          color="text.secondary" 
          sx={{ 
            textAlign: 'center', 
            py: isMobile ? 2 : 4,
            fontSize: isMobile ? 12 : 14
          }}
        >
          No se identificaron fortalezas destacadas en este periodo. Continúa trabajando para mejorar.
        </Typography>
      )}
    </Paper>
  );
};

export default Fortalezas;
